import React, { useEffect, useState } from 'react';

import LinkRow from './LinkRow';
import UploadFile from './UploadFile';
import { Banner, BaseButton, useT } from '@repo/common';

import { ReactComponent as InfoIcon } from '@repo/common/assets/icons/20px/icon_20px_info_outline.svg';
import { ReactComponent as FileDraftIcon } from '@repo/common/assets/icons/20px/icon_20px_file-draft_outline.svg';
import { ReactComponent as LinkIcon } from '@repo/common/assets/icons/20px/icon_20px_links.svg';

const AdditionalUpload = ({
  job,
  files,
  links,
  setUploadData,
  submitAttempted,
  onValidityChange,
  status,
  isMyPage = false,
  $isMobile,
}) => {
  const t = useT();
  const [activeTab, setActiveTab] = useState('file');

  /**
   * 공고 상세 API는 `portfolioSubmission`으로 내려준다.
   * (기존 `portfolioRequired` 참조는 항상 undefined라 배너가 뜨지 않았음)
   */
  const portfolioRequired = Boolean(
    job?.portfolioSubmission ?? job?.portfolioRequired,
  );

  /**
   * 공고 게시 단계에서 선택한 필수 제출 자료 유형.
   * (Portfolio / Cover letter / Writing sample / Github·Tech blog / Case study /
   *  Certificate / Transcript / Video introduction / Visa document 등)
   * 아직 유형 선택 필드가 없는 공고는 Portfolio로 폴백한다.
   */
  const requiredMaterialLabel = job?.requiredMaterialLabel || 'portfolio';

  const hasValidFile = files.some((item) => !item.error);
  const hasValidLink = links.some(
    (link) => link?.linkUrl && link.linkUrl.trim() !== '',
  );
  const isInvalid =
    portfolioRequired && files.length === 0 && !hasValidFile && !hasValidLink;

  const hasFiles = files && files.length > 0;
  const hasLinks = links && links.length > 0;
  const hasData = hasFiles || hasLinks;

  useEffect(() => {
    onValidityChange?.(isInvalid);
  }, [isInvalid, onValidityChange]);

  const setFiles = (value) => {
    setUploadData((prev) => ({
      ...prev,
      files: typeof value === 'function' ? value(prev.files) : value,
    }));
  };

  const setLinks = (value) => {
    setUploadData((prev) => ({
      ...prev,
      links: typeof value === 'function' ? value(prev.links) : value,
    }));
  };

  return (
    <div className='flex flex-col items-start tabletH:py-6 w-full'>
      <div className='typo-b14Strong tabletH:typo-b16Strong text-text-primary pb-3 tabletH:pb-2'>
        {status === 'view' ? t('Additional Materials', '추가 자료') : t('Additional Upload', '추가 업로드')}
      </div>

      {status === 'edit' && portfolioRequired && (
        <div className='w-full pb-4'>
          <Banner
            size='s'
            type={submitAttempted && isInvalid ? 'error' : 'info'}
            className='py-2'
            hideClose
            icon={<InfoIcon />}
            label={t(
              `A ${requiredMaterialLabel} is required to apply. Upload a file or add a link to continue.`,
              `지원하려면 ${requiredMaterialLabel}이(가) 필요합니다. 파일을 업로드하거나 링크를 추가해 주세요.`,
            )}
          />
        </div>
      )}

      {status === 'edit' ? (
        <div className='flex flex-col gap-3 tabletH:gap-4 items-start w-full text-text-tertiary'>
          <div className='flex w-full'>
            <BaseButton
              type='text'
              size='s'
              hideIcon
              typo={false}
              isStatic
              onClick={() => setActiveTab('file')}
              textWrapperClass='h-auto'
              className={`h-auto w-full min-w-0 cursor-pointer rounded-none border-0 bg-transparent p-0 pb-2 text-center typo-b13Strong tabletH:typo-b14Strong transition-colors
            ${
              activeTab === 'file'
                ? 'text-text-primary border-b-2 border-grayscale-gray07'
                : 'text-text-placeholder border-b-2 border-grayscale-gray03'
            }
          `}
            >
              {t('Upload File', '파일 업로드')}
            </BaseButton>

            <BaseButton
              type='text'
              size='s'
              hideIcon
              typo={false}
              isStatic
              onClick={() => setActiveTab('link')}
              textWrapperClass='h-auto'
              className={`h-auto w-full min-w-0 cursor-pointer rounded-none border-0 bg-transparent p-0 pb-2 text-center typo-b13Strong tabletH:typo-b14Strong transition-colors
            ${
              activeTab === 'link'
                ? 'text-text-primary border-b-2 border-grayscale-gray07'
                : 'text-text-placeholder border-b-2 border-grayscale-gray03'
            }
          `}
            >
              {t('Add a link', '링크 추가')}
            </BaseButton>
          </div>

          {activeTab === 'file' && (
            <UploadFile
              files={files}
              setFiles={setFiles}
              jobId={isMyPage ? job?.jobId : job?.id}
              $isMobile={$isMobile}
            />
          )}

          {activeTab === 'link' && (
            <LinkRow links={links} setLinks={setLinks} />
          )}
        </div>
      ) : (
        <div className='flex flex-col w-full'>
          {!hasData ? (
            <div className='flex items-center gap-2 text-text-placeholder typo-b14'>
              {t('No files or links added', '추가된 파일이나 링크가 없습니다')}
            </div>
          ) : (
            <>
              {hasFiles && (
                <div className='flex flex-col'>
                  {files.map((file) => (
                    <div key={file.id}>
                      <div className='flex py-2 gap-2'>
                        <div className='p-0.5 tabletH:px-0.5 tabletH:py-0'>
                          <FileDraftIcon className='text-icon-placeholder size-4 tabletH:size-5' />
                        </div>
                        <span className='typo-b12 tabletH:typo-b14 text-text-placeholder'>
                          {file.file?.name}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {hasLinks && (
                <div className='flex flex-col'>
                  {links.map((link) => (
                    <div key={link.id} className='flex items-center'>
                      <div className='flex py-2 gap-2'>
                        <div className='p-0.5 tabletH:px-0.5 tabletH:py-0'>
                          <LinkIcon className='text-icon-placeholder size-4 tabletH:size-5' />
                        </div>
                        <div className='flex h-5 px-1.5 justify-center items-center rounded bg-grayscale-gray02 w-full'>
                          <span className='typo-b11Strong tabletH:typo-b12Strong text-text-placeholder'>
                            {link.linkType || t('Link', '링크')}
                          </span>
                        </div>
                        <span className='typo-b12 tabletH:typo-b14 text-text-placeholder w-full'>
                          {link.linkUrl}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default AdditionalUpload;
